import type { PanderrTemplateDefinition } from "../types";

export const MUNICIPALITY_MODERN_TEMPLATE: PanderrTemplateDefinition = {
  id: "municipality-modern",
  name: "Municipality Modern",
  description:
    "An accessible, information-first civic website for towns, cities, and local government.",
  category: "municipality",

  navigation: [
    { label: "Government", href: "/government/" },
    { label: "Departments", href: "/departments/" },
    { label: "Agendas & Minutes", href: "/agendas-minutes/" },
    { label: "Documents & Forms", href: "/documents-forms/" },
    { label: "Residents", href: "/residents/" },
    { label: "Businesses", href: "/businesses/" },
    { label: "Contact", href: "/contact/" },
  ],

  headerCta: {
    label: "Public Documents",
    href: "/documents-forms/",
    variant: "primary",
  },

  footer: {
    description:
      "Official information, public resources, services, and updates for our community.",
    columns: [
      {
        heading: "Government",
        links: [
          { label: "Government", href: "/government/" },
          { label: "Departments", href: "/departments/" },
          { label: "Agendas & Minutes", href: "/agendas-minutes/" },
        ],
      },
      {
        heading: "Resources",
        links: [
          { label: "Documents & Forms", href: "/documents-forms/" },
          { label: "Public Notices", href: "/documents-forms/" },
          { label: "Contact", href: "/contact/" },
        ],
      },
      {
        heading: "Community",
        links: [
          { label: "Residents", href: "/residents/" },
          { label: "Businesses", href: "/businesses/" },
          { label: "Community Information", href: "/residents/" },
        ],
      },
    ],
  },

  sections: [
    {
      id: "hero",
      type: "hero",
      eyebrow: "Welcome to our community",
      heading: "Local government. Clear information. Connected community.",
      body: "Find city services, public meetings, documents, forms, department information, and updates in one easy-to-use place.",
      images: [
        {
          src: "/studio/assets/stock/images/gov_image.jpg",
          alt: "View of a city and community",
          role: "hero",
        },
      ],
      buttons: [
        {
          label: "Find City Services",
          href: "/departments/",
          variant: "primary",
        },
        {
          label: "View Agendas & Minutes",
          href: "/agendas-minutes/",
          variant: "secondary",
        },
      ],
    },

    {
      id: "services",
      type: "services",
      eyebrow: "Quick access",
      heading: "Find what you need faster.",
      body: "Start with the most commonly requested government information and public services.",
      services: [
        {
          title: "Departments",
          description:
            "Find department information, responsibilities, services, and contact details.",
          href: "/departments/",
        },
        {
          title: "Agendas & Minutes",
          description:
            "Access upcoming meeting agendas, approved minutes, and public meeting information.",
          href: "/agendas-minutes/",
        },
        {
          title: "Documents & Forms",
          description:
            "Download applications, permits, budgets, forms, notices, and other public documents.",
          href: "/documents-forms/",
        },
      ],
    },

    {
      id: "about",
      type: "about",
      eyebrow: "Your local government",
      heading: "Serving residents with accessible, dependable information.",
      body: "Our website is designed to help residents, businesses, visitors, and community partners quickly understand local government and access the resources they need.",
      images: [
        {
          src: "/studio/assets/stock/images/small_town.jpg",
          alt: "Community leaders meeting together",
          role: "about",
        },
      ],
      buttons: [
        {
          label: "Learn About Government",
          href: "/government/",
          variant: "secondary",
        },
      ],
    },

    {
      id: "process",
      type: "process",
      eyebrow: "Stay informed",
      heading: "Participating in local government should be straightforward.",
      body: "Use the website to stay informed about meetings, public records, notices, and community decisions.",
      steps: [
        {
          title: "Find the information",
          description:
            "Browse departments, meetings, documents, and community resources.",
        },
        {
          title: "Review public materials",
          description:
            "Access agendas, minutes, notices, forms, budgets, and other public records.",
        },
        {
          title: "Connect with the city",
          description:
            "Contact the appropriate department or local government office when you need help.",
        },
      ],
    },

    {
      id: "cta",
      type: "cta",
      eyebrow: "Public information",
      heading: "Looking for a document, form, or meeting record?",
      body: "Visit the public documents section to find agendas, minutes, forms, notices, budgets, and downloadable resources.",
      buttons: [
        {
          label: "Browse Public Documents",
          href: "/documents-forms/",
          variant: "primary",
        },
      ],
    },
  ],

  pages: [
    {
      id: "government",
      name: "Government",
      title: "Government",
      slug: "/government/",
      sections: [
        {
          id: "government-hero",
          type: "hero",
          eyebrow: "Local government",
          heading: "Leadership, public meetings, and local decision-making.",
          body: "Learn how your local government is organized, who serves the community, and how public decisions are made.",
          images: [
            {
              src: "/studio/assets/stock/images/six people in a business meeting.jpg",
              alt: "Local government meeting",
              role: "hero",
            },
          ],
        },
        {
          id: "government-about",
          type: "about",
          eyebrow: "How government works",
          heading: "Transparent government starts with accessible information.",
          body: "Use this page for elected officials, council information, boards, commissions, meeting schedules, ordinances, and other government resources.",
          images: [
            {
              src: "/studio/assets/stock/images/two people in a meeting holding pens.jpg",
              alt: "Officials reviewing information during a meeting",
              role: "about",
            },
          ],
        },
        {
          id: "government-services",
          type: "services",
          eyebrow: "Government resources",
          heading: "Explore local government.",
          services: [
            {
              title: "Mayor & Council",
              description:
                "Introduce elected officials and explain roles, terms, meetings, and responsibilities.",
              href: "/government/",
            },
            {
              title: "Boards & Commissions",
              description:
                "Provide information about public boards, commissions, appointments, and meeting schedules.",
              href: "/government/",
            },
            {
              title: "Agendas & Minutes",
              description:
                "Access official public meeting records and upcoming agenda materials.",
              href: "/agendas-minutes/",
            },
          ],
        },
        {
          id: "government-cta",
          type: "cta",
          eyebrow: "Public meetings",
          heading: "Stay informed about local decisions.",
          body: "Review meeting agendas and minutes to follow the work of your local government.",
          buttons: [
            {
              label: "View Agendas & Minutes",
              href: "/agendas-minutes/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "departments",
      name: "Departments",
      title: "Departments",
      slug: "/departments/",
      sections: [
        {
          id: "departments-hero",
          type: "hero",
          eyebrow: "City departments",
          heading: "Connect with the department that can help.",
          body: "Find department responsibilities, public services, contact information, hours, and useful resources.",
          images: [
            {
              src: "/studio/assets/stock/images/small_town_corner.jpg",
              alt: "Government office workspace",
              role: "hero",
            },
          ],
        },
        {
          id: "departments-list",
          type: "services",
          eyebrow: "Departments & services",
          heading: "Local services in one place.",
          body: "Customize these cards for your municipality's departments and public-service structure.",
          services: [
            {
              title: "Administration",
              description:
                "City administration, clerk services, records, finance, and general government information.",
              href: "/contact/",
            },
            {
              title: "Public Works",
              description:
                "Streets, sanitation, infrastructure, utilities, facilities, and related public services.",
              href: "/contact/",
            },
            {
              title: "Planning & Development",
              description:
                "Planning, zoning, permits, inspections, development, and community growth resources.",
              href: "/businesses/",
            },
          ],
        },
        {
          id: "departments-faq",
          type: "faq",
          eyebrow: "Need help?",
          heading: "Finding the right department.",
          faqs: [
            {
              question: "Who should I contact for a city service?",
              answer:
                "Use the department listings and contact information on this page to find the office responsible for the service you need.",
            },
            {
              question: "Where can I find forms and applications?",
              answer:
                "Visit Documents & Forms for downloadable applications, permits, forms, and public resources.",
            },
            {
              question: "Where can I find meeting information?",
              answer:
                "Visit Agendas & Minutes for meeting schedules, agendas, minutes, and related public records.",
            },
          ],
        },
      ],
    },

    {
      id: "agendas-minutes",
      name: "Agendas & Minutes",
      title: "Agendas & Minutes",
      slug: "/agendas-minutes/",
      sections: [
        {
          id: "meetings-hero",
          type: "hero",
          eyebrow: "Public meetings",
          heading: "Agendas, minutes, and meeting information.",
          body: "Access upcoming meeting agendas, approved minutes, meeting schedules, and other official public records.",
          images: [
            {
              src: "/studio/assets/stock/images/gov_image4.jpg",
              alt: "People reviewing meeting documents",
              role: "hero",
            },
          ],
        },
        {
          id: "meetings-services",
          type: "services",
          eyebrow: "Meeting records",
          heading: "Find public meeting information.",
          services: [
            {
              title: "Upcoming Agendas",
              description:
                "Post meeting dates, agenda packets, supporting documents, and public notices.",
              href: "/agendas-minutes/",
            },
            {
              title: "Approved Minutes",
              description:
                "Provide downloadable approved minutes and historical meeting records.",
              href: "/agendas-minutes/",
            },
            {
              title: "Meeting Calendar",
              description:
                "Share upcoming council, board, commission, and public hearing dates.",
              href: "/agendas-minutes/",
            },
          ],
        },
        {
          id: "meetings-faq",
          type: "faq",
          eyebrow: "Meeting information",
          heading: "Frequently requested information.",
          faqs: [
            {
              question: "When are agendas posted?",
              answer:
                "Customize this answer with your municipality's posting schedule and public notice requirements.",
            },
            {
              question: "Where can I download meeting minutes?",
              answer:
                "Approved meeting minutes can be posted and organized here for public download.",
            },
            {
              question: "How can I attend a public meeting?",
              answer:
                "Customize this answer with meeting locations, accessibility details, livestream information, and public-comment procedures.",
            },
          ],
        },
      ],
    },

    {
      id: "documents-forms",
      name: "Documents & Forms",
      title: "Documents & Forms",
      slug: "/documents-forms/",
      sections: [
        {
          id: "documents-hero",
          type: "hero",
          eyebrow: "Public documents",
          heading: "Forms, applications, notices, budgets, and public records.",
          body: "Find frequently requested documents and downloadable resources organized in one accessible location.",
          images: [
            {
              src: "/studio/assets/stock/images/gov_image1.jpg",
              alt: "Office workspace used for public records and documents",
              role: "hero",
            },
          ],
        },
        {
          id: "documents-services",
          type: "services",
          eyebrow: "Document library",
          heading: "Quick access to public resources.",
          services: [
            {
              title: "Forms & Applications",
              description:
                "Permits, applications, requests, registrations, and other commonly used forms.",
              href: "/documents-forms/",
            },
            {
              title: "Budgets & Financial Documents",
              description:
                "Approved budgets, audits, financial reports, and other fiscal documents.",
              href: "/documents-forms/",
            },
            {
              title: "Public Notices",
              description:
                "Legal notices, hearings, announcements, bids, and other public postings.",
              href: "/documents-forms/",
            },
          ],
        },
        {
          id: "documents-faq",
          type: "faq",
          eyebrow: "Document help",
          heading: "Can't find what you're looking for?",
          faqs: [
            {
              question: "Are documents available as PDFs?",
              answer:
                "Yes. Public documents can be made available as accessible downloadable PDF files.",
            },
            {
              question: "How do I request a public record?",
              answer:
                "Customize this answer with the municipality's open-records request process and contact information.",
            },
            {
              question: "Who can help me find an older document?",
              answer:
                "Provide the city clerk or records contact information for archived and historical public records.",
            },
          ],
        },
      ],
    },

    {
      id: "residents",
      name: "Residents",
      title: "Residents",
      slug: "/residents/",
      sections: [
        {
          id: "residents-hero",
          type: "hero",
          eyebrow: "Resident resources",
          heading:
            "Information and services for everyday life in our community.",
          body: "Find utilities, sanitation, permits, parks, public safety, community information, and other useful resident resources.",
          images: [
            {
              src: "/studio/assets/stock/images/gov_image3.jpg",
              alt: "Local community and city view",
              role: "hero",
            },
          ],
        },
        {
          id: "residents-services",
          type: "services",
          eyebrow: "Resident services",
          heading: "Popular community resources.",
          services: [
            {
              title: "Utilities & Services",
              description:
                "Water, sewer, sanitation, billing, service requests, and other municipal services.",
              href: "/departments/",
            },
            {
              title: "Parks & Community",
              description:
                "Parks, recreation, events, facilities, programs, and community resources.",
              href: "/residents/",
            },
            {
              title: "Permits & Forms",
              description:
                "Find applications, permits, registrations, and frequently requested resident forms.",
              href: "/documents-forms/",
            },
          ],
        },
        {
          id: "residents-cta",
          type: "cta",
          eyebrow: "Need assistance?",
          heading: "We're here to help residents find the right resource.",
          body: "Contact the appropriate department or city office if you need help with a local service.",
          buttons: [
            {
              label: "Contact City Hall",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "businesses",
      name: "Businesses",
      title: "Businesses",
      slug: "/businesses/",
      sections: [
        {
          id: "businesses-hero",
          type: "hero",
          eyebrow: "Business resources",
          heading:
            "Helping businesses start, grow, and invest in our community.",
          body: "Find licensing, permitting, planning, development, zoning, procurement, and business-related government resources.",
          images: [
            {
              src: "/studio/assets/stock/images/tim-mossholder-qvWnGmoTbik-unsplash.jpg",
              alt: "Commercial development and city view",
              role: "hero",
            },
          ],
        },
        {
          id: "business-services",
          type: "services",
          eyebrow: "Business services",
          heading: "Resources for businesses and development.",
          services: [
            {
              title: "Business Licensing",
              description:
                "License requirements, applications, renewals, and local business regulations.",
              href: "/documents-forms/",
            },
            {
              title: "Planning & Zoning",
              description:
                "Development requirements, zoning information, applications, and planning resources.",
              href: "/departments/",
            },
            {
              title: "Permits & Inspections",
              description:
                "Building permits, inspections, applications, and development-related forms.",
              href: "/documents-forms/",
            },
          ],
        },
        {
          id: "business-cta",
          type: "cta",
          eyebrow: "Planning a project?",
          heading: "Connect with the city before you get started.",
          body: "Reach out for guidance about licensing, development, permits, planning, or other local requirements.",
          buttons: [
            {
              label: "Contact the City",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "contact",
      name: "Contact",
      title: "Contact",
      slug: "/contact/",
      sections: [
        {
          id: "contact-hero",
          type: "hero",
          eyebrow: "Contact your city",
          heading: "We're here to help you find the right information.",
          body: "Contact City Hall or the appropriate department for assistance with services, records, meetings, permits, or general questions.",
          images: [
            {
              src: "/studio/assets/stock/images/tookapic-office-g11734fe79_1920_original.jpg",
              alt: "Local government office",
              role: "hero",
            },
          ],
        },
        {
          id: "contact-details",
          type: "contact",
          eyebrow: "City Hall",
          heading: "Contact information.",
          body: "Add the municipality's address, main phone number, office hours, email address, accessibility information, and department contacts in Studio.",
          buttons: [
            {
              label: "Call City Hall",
              href: "tel:+15555555555",
              hrefField: "site.contact.phone",
              variant: "primary",
            },
            {
              label: "Email City Hall",
              href: "mailto:cityhall@example.gov",
              hrefField: "site.contact.email",
              variant: "secondary",
            },
          ],
        },
        {
          id: "contact-faq",
          type: "faq",
          eyebrow: "Before you contact us",
          heading: "Helpful information.",
          faqs: [
            {
              question: "What are City Hall's hours?",
              answer:
                "Add the municipality's normal business hours and holiday schedule here.",
            },
            {
              question: "How do I request an accommodation?",
              answer:
                "Add the municipality's accessibility and reasonable-accommodation contact process here.",
            },
            {
              question: "Where can I submit a public records request?",
              answer:
                "Add the municipality's open-records request instructions and records contact information here.",
            },
          ],
        },
      ],
    },
  ],
};
